<template>
  <div id="app" :class="{ 'app-fullscreen-chat': isJobRecommendationsPage }">
    <!-- Optional announcement strip (DeepSeek-style); hidden on job-recommendations. Use <a> + push to avoid Vue Router "Invalid linked format" on invalid :to. -->
    <div v-if="config.ui.announcement && !isJobRecommendationsPage" class="announcement-strip">
      <a :href="announcementTo" class="announcement-link" @click.prevent="$router.push(announcementTo)">
        <span class="announcement-text">{{ $t('app.announcementText') }}</span>
        <span class="announcement-cta">{{ $t('app.announcementLinkLabel') }}</span>
      </a>
    </div>
    <!-- Chat page header (DeepSeek-style): logo + account icon always visible -->
    <header v-if="isJobRecommendationsPage" class="app-header-minimal app-header-chat">
      <div class="header-content">
        <router-link to="/" class="header-logo">{{ $t('app.headerLogo') }}</router-link>
        <div class="header-right">
          <router-link to="/job-recommendations" class="header-link nav-promo" data-testid="nav-job-recommendations">{{ $t('app.navJobRecommendations') }}</router-link>
          <router-link to="/interview-debrief" class="header-link nav-promo" data-testid="nav-interview-debrief">{{ $t('app.navInterviewDebrief') }}</router-link>
          <LocaleToggle />
          <div v-if="isAuthenticated" class="header-user">
            <div class="user-dropdown-minimal">
              <button class="header-user-btn" @click="toggleUserMenu" type="button" data-testid="job-recommendations-account-icon">
                <span class="user-avatar-mini">{{ userInitial }}</span>
              </button>
              <div v-if="showUserMenu" class="dropdown-menu">
                <div class="dropdown-item user-info-item">
                  <span class="user-name">{{ userDisplayName }}</span>
                </div>
                <div class="dropdown-item credits-in-menu">
                  <CreditBalance
                    :show-history-button="true"
                    :auto-refresh="true"
                    @show-history="showCreditHistory"
                  />
                </div>
                <router-link to="/resume-library" class="dropdown-item" @click="showUserMenu = false">
                  <span class="dropdown-icon">📁</span>
                  {{ $t('app.resumeLibrary') }}
                </router-link>
                <router-link to="/settings" class="dropdown-item" @click="showUserMenu = false">
                  <span class="dropdown-icon">⚙️</span>
                  {{ $t('notion.settings') }}
                </router-link>
                <div class="dropdown-item" @click="handleInviteFromMenu">
                  <span class="dropdown-icon">✉️</span>
                  {{ $t('app.inviteFriends') }}
                </div>
                <div class="dropdown-divider"></div>
                <div class="dropdown-item logout" @click="handleLogout">
                  <span class="dropdown-icon">🚪</span>
                  {{ $t('common.logout') }}
                </div>
              </div>
            </div>
          </div>
          <div v-else class="header-auth">
            <button class="header-link-btn" @click="showAuthModal('login')" type="button" data-testid="header-login-button">{{ $t('common.login') }}</button>
            <button class="header-link-btn primary" @click="showAuthModal('register')" type="button" data-testid="header-register-button">{{ $t('common.register') }}</button>
          </div>
        </div>
      </div>
    </header>
    <!-- Main site header -->
    <header v-else class="app-header-minimal">
      <div class="header-content">
        <router-link to="/" class="header-logo">{{ $t('app.headerLogo') }}</router-link>
        <div class="header-right">
          <router-link to="/job-recommendations" class="header-link nav-promo" data-testid="nav-job-recommendations">{{ $t('app.navJobRecommendations') }}</router-link>
          <router-link to="/interview-debrief" class="header-link nav-promo" data-testid="nav-interview-debrief">{{ $t('app.navInterviewDebrief') }}</router-link>
          <LocaleToggle />
          <div v-if="isAuthenticated" class="header-user">
            <div class="user-dropdown-minimal">
              <button class="header-user-btn" @click="toggleUserMenu" type="button">
                <span class="user-avatar-mini">{{ userInitial }}</span>
              </button>
              <div v-if="showUserMenu" class="dropdown-menu">
                <div class="dropdown-item user-info-item">
                  <span class="user-name">{{ userDisplayName }}</span>
                </div>
                <div class="dropdown-item credits-in-menu">
                  <CreditBalance 
                    :show-history-button="true"
                    :auto-refresh="true"
                    @show-history="showCreditHistory"
                  />
                </div>
                <router-link to="/resume-library" class="dropdown-item" @click="showUserMenu = false">
                  <span class="dropdown-icon">📁</span>
                  {{ $t('app.resumeLibrary') }}
                </router-link>
                <router-link to="/settings" class="dropdown-item" @click="showUserMenu = false">
                  <span class="dropdown-icon">⚙️</span>
                  {{ $t('notion.settings') }}
                </router-link>
                <div class="dropdown-item" @click="handleInviteFromMenu">
                  <span class="dropdown-icon">✉️</span>
                  {{ $t('app.inviteFriends') }}
                </div>
                <div class="dropdown-divider"></div>
                <div class="dropdown-item logout" @click="handleLogout">
                  <span class="dropdown-icon">🚪</span>
                  {{ $t('common.logout') }}
                </div>
              </div>
            </div>
          </div>
          <div v-else class="header-auth">
            <button class="header-link-btn" @click="showAuthModal('login')" type="button" data-testid="header-login-button">{{ $t('common.login') }}</button>
            <button class="header-link-btn primary" @click="showAuthModal('register')" type="button" data-testid="header-register-button">{{ $t('common.register') }}</button>
          </div>
        </div>
      </div>
    </header>
    <main>
      <router-view />
    </main>
    <footer class="app-footer" v-if="config.footer.showFooter && !isJobRecommendationsPage">
      <div class="footer-content">
        <div class="footer-brand">{{ $t('app.footerCopyPrefix', { year: currentYear }) }}<a href="https://www.deepseek.com/en/" target="_blank" rel="noopener">DeepSeek</a>{{ $t('app.footerCopySuffix') }}</div>
        <div class="footer-links">
          <div class="footer-group">
            <span class="footer-group-label">{{ $t('app.footerProduct') }}</span>
            <router-link to="/job-recommendations">{{ $t('app.navJobRecommendations') }}</router-link>
            <router-link to="/job-recommendations?mode=skill-advice">{{ $t('app.footerSkillAdvice') }}</router-link>
            <router-link to="/interview-debrief">{{ $t('app.navInterviewDebrief') }}</router-link>
          </div>
          <div class="footer-group">
            <span class="footer-group-label">{{ $t('app.footerLegalSafety') }}</span>
            <a href="#" @click.prevent="showTermsModal">{{ $t('app.termsOfService') }}</a>
            <a href="#" @click.prevent="showPrivacyModal">{{ $t('app.privacyPolicy') }}</a>
          </div>
        </div>
        <div class="footer-ids" v-if="showIds">
          <a :href="icpLink" class="icp-id" target="_blank" rel="noopener">{{ config.footer.icpId }}</a>
          <img :src="config.footer.policeLogoPath" :alt="$t('app.policeAlt')" class="police-logo" v-if="config.footer.policeLogoPath">
          <a :href="policeLink" class="police-id" target="_blank" rel="noopener">{{ config.footer.policeId }}</a>
        </div>
      </div>
    </footer>
    
    <LegalDocumentModal 
      :visible="showLegalModal" 
      :documentType="modalDocumentType"
      @close="closeLegalModal"
    />
    
    <AuthModal
      :isVisible="showAuthModalVisible"
      :defaultMode="authModalMode"
      @close="closeAuthModal"
      @success="handleAuthSuccess"
    />
    
    <InvitationModal
      :isVisible="showInvitationModalVisible"
      @close="closeInvitationModal"
      @success="handleInvitationSuccess"
    />
    
    <CreditHistoryModal
      :isVisible="showCreditHistoryModal"
      @close="closeCreditHistoryModal"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useAuthStore } from './stores/auth'
import { useLocaleStore } from './stores/locale'
import { useCreditStore } from './stores/credits'
import config from './config.js'
import LegalDocumentModal from './components/LegalDocumentModal.vue'
import AuthModal from './components/AuthModal.vue'
import InvitationModal from './components/InvitationModal.vue'
import CreditBalance from './components/CreditBalance.vue'
import CreditHistoryModal from './components/CreditHistoryModal.vue'
import LocaleToggle from './components/LocaleToggle.vue'

export default {
  name: 'App',
  components: {
    LegalDocumentModal,
    AuthModal,
    InvitationModal,
    CreditBalance,
    CreditHistoryModal,
    LocaleToggle
  },
  data() {
    return {
      config: config,
      showLegalModal: false,
      modalDocumentType: 'terms',
      showAuthModalVisible: false,
      authModalMode: 'login',
      showUserMenu: false,
      showInvitationModalVisible: false,
      showCreditHistoryModal: false
    }
  },
  computed: {
    ...mapState(useAuthStore, ['isAuthenticated', 'user', 'isLoading']),
    isJobRecommendationsPage() {
      return this.$route?.path === '/job-recommendations' || this.$route?.path === '/interview-debrief'
    },
    showIds() {
      return this.config.footer.icpId || this.config.footer.policeId || this.config.footer.policeLogoPath
    },
    icpLink() {
      return 'https://beian.miit.gov.cn'
    },
    policeLink() {
      return 'https://beian.mps.gov.cn'
    },
    userDisplayName() {
      if (!this.user) return this.$t('common.userFallback')
      if (this.user.profile?.firstName) {
        return this.user.profile.firstName
      }
      return this.user.username || this.user.email?.split('@')[0] || this.$t('common.userFallback')
    },
    userInitial() {
      if (!this.user) return 'U'
      const name = this.userDisplayName
      return name.charAt(0).toUpperCase()
    },
    currentYear() {
      return new Date().getFullYear()
    },
    // Valid path for announcement router-link only (Vue Router rejects '#' or non-path values)
    announcementTo() {
      if (!this.config?.ui?.announcement) return '/'
      const link = this.config.ui.announcement.link
      if (typeof link === 'string') {
        const path = link.trim()
        if (path && path.startsWith('/')) return path
      }
      return '/'
    }
  },
  methods: {
    ...mapActions(useAuthStore, ['logout', 'initialize']),
    showTermsModal() {
      this.modalDocumentType = 'terms'
      this.showLegalModal = true
    },
    showPrivacyModal() {
      this.modalDocumentType = 'privacy'
      this.showLegalModal = true
    },
    closeLegalModal() {
      this.showLegalModal = false
    },
    showAuthModal(mode = 'login') {
      this.authModalMode = mode
      this.showAuthModalVisible = true
    },
    closeAuthModal() {
      this.showAuthModalVisible = false
    },
    handleAuthSuccess() {
      this.closeAuthModal()
      // Show welcome message or redirect if needed
      console.log('Authentication successful')
    },
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu
    },
    // Profile and Settings methods disabled - UI buttons hidden until features are implemented
    // goToProfile() {
    //   this.showUserMenu = false
    //   // TODO: Implement profile page navigation
    //   console.log('Navigate to profile')
    // },
    // showSettings() {
    //   this.showUserMenu = false
    //   // TODO: Implement settings page
    //   console.log('Show settings')
    // },
    showInvitationModal() {
      this.showInvitationModalVisible = true
    },
    closeInvitationModal() {
      this.showInvitationModalVisible = false
    },
    handleInvitationSuccess(result) {
      console.log('Invitation sent successfully:', result)
      // Could show a toast notification here
    },
    showCreditHistory() {
      this.showCreditHistoryModal = true
    },
    closeCreditHistoryModal() {
      this.showCreditHistoryModal = false
    },
    async handleLogout() {
      this.showUserMenu = false
      await this.logout()
    },
    handleInviteFromMenu() {
      this.showUserMenu = false
      this.showInvitationModal()
    },
    handleClickOutside(event) {
      if (!event.target.closest('.user-dropdown-minimal')) {
        this.showUserMenu = false
      }
    },
    handleOAuthCallback() {
      // Check if we're on the callback route
      if (this.$route.path === '/auth/callback') {
        const params = this.$route.query
        
        // Handle OAuth success
        if (params.success === 'true' && params.provider === 'google') {
          try {
            // Parse tokens and user from query params
            if (params.tokens) {
              const tokens = JSON.parse(decodeURIComponent(params.tokens))
              const authStore = useAuthStore()
              authStore.setTokens(tokens)
              
              if (params.user) {
                const user = JSON.parse(decodeURIComponent(params.user))
                authStore.setUser(user)
              }
              
              // Clear OAuth state from sessionStorage
              sessionStorage.removeItem('oauth_state')
              sessionStorage.removeItem('oauth_provider')
              
              // Redirect to home page
              this.$router.replace('/')
              
              // Show success message
              console.log('Google OAuth login successful')
            }
          } catch (error) {
            console.error('Error processing OAuth callback:', error)
            this.showAuthModal('login')
          }
        } else if (params.error) {
          // Handle OAuth error
          const errorMsg = decodeURIComponent(params.error)
          console.error('OAuth error:', errorMsg)
          
          // Show auth modal with error
          this.authModalMode = 'login'
          this.showAuthModalVisible = true
          
          // Clear OAuth state
          sessionStorage.removeItem('oauth_state')
          sessionStorage.removeItem('oauth_provider')
        }
      }
    }
  },
  async mounted() {
    // Initialize locale store (persistence + browser detection)
    useLocaleStore().init()
    // Initialize auth store
    this.initialize()
    
    // Initialize credit store if authenticated
    if (this.isAuthenticated) {
      const creditStore = useCreditStore()
      try {
        await creditStore.fetchBalance()
      } catch (error) {
        console.error('Failed to fetch credit balance on mount:', error)
      }
    }
    
    // Handle OAuth callback
    this.handleOAuthCallback()
    
    // Add click outside listener for dropdown
    document.addEventListener('click', this.handleClickOutside)
    
    // Watch for auth state changes to fetch/clear credits
    this.$watch('isAuthenticated', async (isAuth) => {
      const creditStore = useCreditStore()
      if (isAuth) {
        try {
          await creditStore.fetchBalance()
        } catch (error) {
          console.error('Failed to fetch credit balance after login:', error)
        }
      } else {
        creditStore.clearCredits()
      }
    })
    
    // Watch for fullscreen chat pages to lock body scroll (Doubao-style)
    this.$watch('isJobRecommendationsPage', (isFullscreen) => {
      if (isFullscreen) {
        document.documentElement.classList.add('fullscreen-chat-active')
        document.body.classList.add('fullscreen-chat-active')
      } else {
        document.documentElement.classList.remove('fullscreen-chat-active')
        document.body.classList.remove('fullscreen-chat-active')
      }
    }, { immediate: true })
  },
  beforeUnmount() {
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside)
    // Clean up fullscreen chat class
    document.documentElement.classList.remove('fullscreen-chat-active')
    document.body.classList.remove('fullscreen-chat-active')
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: var(--font-body);
  background-color: var(--color-surface);
  color: var(--color-text);
  line-height: 1.6;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Fullscreen chat mode (job-recommendations, interview-debrief): Doubao-style fixed viewport */
/* Lock html and body to prevent any scroll/shake */
html.fullscreen-chat-active,
html.fullscreen-chat-active body {
  height: 100%;
  height: 100vh;
  overflow: hidden;
  position: fixed;
  width: 100%;
  /* Prevent all overscroll effects */
  overscroll-behavior: none;
  -webkit-overflow-scrolling: none;
  /* Prevent pull-to-refresh */
  touch-action: none;
}

/* Re-enable touch for the app container so internal scrolling works */
html.fullscreen-chat-active #app {
  touch-action: auto;
}

#app.app-fullscreen-chat {
  height: 100vh;
  max-height: 100vh;
  min-height: 100vh;
  overflow: hidden;
}

#app.app-fullscreen-chat > header {
  flex-shrink: 0;
}

#app.app-fullscreen-chat > main {
  flex: 1;
  min-height: 0;
  overflow: hidden; /* Let child components handle their own scrolling */
}

/* Mobile: Fullscreen chat uses dynamic viewport height and safe-area insets */
@media (max-width: 768px) {
  html.fullscreen-chat-active,
  html.fullscreen-chat-active body {
    height: 100dvh;
  }

  #app.app-fullscreen-chat {
    height: 100dvh;
    max-height: 100dvh;
    min-height: 100dvh;
    /* Safe area insets for notched devices */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }
}

.announcement-strip {
  background: linear-gradient(90deg, #0c4a6e 0%, #0369a1 100%);
  color: #e0f2fe;
  padding: 0.6rem 1rem;
  text-align: center;
  font-size: 0.9rem;
}

.announcement-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: inherit;
  text-decoration: none;
  transition: opacity 0.2s;
}

.announcement-link:hover {
  opacity: 0.9;
  color: inherit;
}

.announcement-cta {
  font-weight: 600;
  text-decoration: underline;
}

/* Minimal header (DeepSeek-inspired): light, low-height */
.app-header-minimal {
  background: var(--color-surface-elevated);
  color: var(--color-text);
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.header-logo {
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-primary);
  text-decoration: none;
  transition: color var(--transition-fast), opacity var(--transition-fast);
}
.header-logo:hover {
  color: var(--color-primary-hover);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-link,
.header-link-btn {
  font-size: 0.9rem;
  color: var(--color-text);
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0;
  transition: color var(--transition-fast);
}
.header-link:hover,
.header-link-btn:hover {
  color: var(--color-primary);
}
.header-link.router-link-active {
  color: var(--color-primary);
  font-weight: 600;
}

.header-link-btn.primary {
  background: var(--gradient-hero);
  color: white;
  padding: 0.35rem 0.85rem;
  border-radius: var(--radius-full);
  font-weight: 600;
}
.header-link-btn.primary:hover {
  opacity: 0.95;
  color: white;
}

.header-user-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--gradient-hero);
  border: none;
  border-radius: 50%;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s;
}
.header-user-btn:hover {
  opacity: 0.9;
}

.user-avatar-mini {
  display: block;
}

.header-auth {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-dropdown-minimal {
  position: relative;
}

/* LocaleToggle in minimal header: dark text/borders for light bg */
.app-header-minimal :deep(.locale-btn) {
  border-color: var(--color-border);
  color: var(--color-text);
}
.app-header-minimal :deep(.locale-btn:hover) {
  background: var(--color-surface-muted);
}
.app-header-minimal :deep(.locale-btn.active) {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}
.app-header-minimal :deep(.locale-sep) {
  color: var(--color-text-muted);
}

.user-name {
  font-size: 14px;
  font-weight: 500;
}

/* CreditBalance inside dropdown: adapt to white bg */
.credits-in-menu :deep(.credit-balance) {
  background: var(--color-surface-muted);
  border-color: var(--color-border);
}
.credits-in-menu :deep(.balance-label),
.credits-in-menu :deep(.balance-amount) {
  color: var(--color-text);
}
.credits-in-menu :deep(.balance-amount) {
  color: var(--color-success);
}
.credits-in-menu :deep(.history-button) {
  background: var(--color-surface);
  border-color: var(--color-border);
  color: var(--color-text);
}
.credits-in-menu :deep(.history-button:hover) {
  background: var(--color-border);
}

.dropdown-item.credits-in-menu {
  cursor: default;
  background: transparent;
}
.dropdown-item.credits-in-menu:hover {
  background: transparent;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--color-surface-elevated);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  color: var(--color-text);
  font-size: 14px;
  transition: background-color var(--transition-normal);
}

.dropdown-item:hover {
  background-color: var(--color-surface);
}

.dropdown-item.logout {
  color: var(--color-error);
}

.dropdown-item.logout:hover {
  background-color: #fee2e2;
}

.dropdown-icon {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.dropdown-divider {
  height: 1px;
  background-color: var(--color-border);
  margin: 4px 0;
}

main {
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

/* Job recommendations: full-viewport chat (no app header/footer) */
.app-fullscreen-chat main {
  padding: 0;
  max-width: none;
  margin: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.app-footer {
  background-color: var(--color-footer-bg);
  color: var(--color-footer-text);
  text-align: center;
  padding: 1.5rem;
  font-size: 0.9rem;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.footer-brand {
  font-size: 0.9rem;
  opacity: 0.95;
}

.footer-brand a {
  color: var(--color-footer-link);
  text-decoration: none;
}

.footer-brand a:hover {
  text-decoration: underline;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
}

.footer-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.footer-group-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.8;
  margin-bottom: 0.25rem;
}

.footer-group a {
  color: var(--color-footer-link);
  text-decoration: none;
  font-size: 0.9rem;
  transition: opacity var(--transition-fast);
}

.footer-group a:hover {
  opacity: 1;
  text-decoration: underline;
}

.footer-ids {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
}

.icp-id, .police-id {
  font-size: 0.8rem;
  opacity: 0.8;
  color: var(--color-footer-text);
  text-decoration: none;
  transition: opacity var(--transition-fast);
}

.icp-id:hover, .police-id:hover {
  opacity: 1;
  text-decoration: underline;
}

.police-logo {
  height: 20px;
  width: auto;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .header-logo {
    font-size: 1.1rem;
  }
  
  .header-right {
    gap: 0.75rem;
  }
  
  .header-link,
  .header-link-btn {
    font-size: 0.85rem;
  }
  
  main {
    padding: 1rem;
  }
}
</style>
